import { api, type ApiEnvelope } from './api';

/**
 * Helper upload file langsung ke MinIO via presigned URL.
 *
 * Flow:
 *   1. Request presigned URL ke Laravel: POST /api/onboarding/upload/presigned
 *   2. PUT file langsung ke MinIO (bypass Laravel — hemat bandwidth & RAM)
 *   3. Return publicUrl untuk disimpan di form
 *
 * Plus helper `compressImage()` untuk resize foto kamera HP yang biasanya
 * ukurannya besar (3-10 MB) jadi max 1920px @ quality 0.85 (~200-500 KB).
 */

export type UploadPurpose = 'ktp' | 'npwp' | 'liveness' | 'signature';

export type PresignResponse = {
    uploadUrl: string;   // PUT ke sini
    publicUrl: string;   // URL final untuk disimpan di form + DB
    key: string;
    contentType: string;
    expiresIn: number;
};

async function requestPresignedUrl(
    purpose: UploadPurpose,
    contentType: string
): Promise<PresignResponse> {
    const res = await api.post<ApiEnvelope<PresignResponse>>(
        '/api/onboarding/upload/presigned',
        { purpose, content_type: contentType }
    );
    if (res.data.status !== 'ok' || !res.data.data) {
        throw new Error(res.data.message ?? 'Gagal minta upload URL');
    }
    return res.data.data;
}

/**
 * Upload file ke MinIO via presigned PUT URL.
 * Throw kalau gagal; return publicUrl kalau sukses.
 */
export async function uploadDirect(
    file: Blob,
    purpose: UploadPurpose,
    contentType?: string
): Promise<string> {
    const ct = contentType || file.type || 'application/octet-stream';
    const presign = await requestPresignedUrl(purpose, ct);

    const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': ct },
        body: file,
    });

    if (!putRes.ok) {
        throw new Error(`Upload gagal: HTTP ${putRes.status} ${putRes.statusText}`);
    }

    return presign.publicUrl;
}

/**
 * Kompres gambar via Canvas — resize ke max dimension + re-encode JPEG
 * dengan quality yang ditentukan. Biasanya foto kamera HP 10MB → 200-500 KB.
 *
 * @param file        Input image File/Blob
 * @param maxSize     Dimensi terpanjang (width atau height). Default 1920.
 * @param quality     JPEG quality 0-1. Default 0.85.
 * @param mime        Output MIME. Default 'image/jpeg'.
 */
export function compressImage(
    file: Blob,
    maxSize: number = 1920,
    quality: number = 0.85,
    mime: string = 'image/jpeg'
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Gagal baca file'));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error('Gagal decode gambar'));
            img.onload = () => {
                const { width: w0, height: h0 } = img;
                let w = w0;
                let h = h0;

                // Scale down kalau melebihi maxSize
                if (Math.max(w, h) > maxSize) {
                    if (w >= h) {
                        h = Math.round((maxSize / w) * h);
                        w = maxSize;
                    } else {
                        w = Math.round((maxSize / h) * w);
                        h = maxSize;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas tidak tersedia'));
                    return;
                }
                ctx.drawImage(img, 0, 0, w, h);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Gagal encode gambar'));
                            return;
                        }
                        resolve(blob);
                    },
                    mime,
                    quality
                );
            };
            img.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    });
}

/**
 * Convert Blob/File ke base64 string (tanpa data URL prefix).
 * Dipakai untuk kirim video liveness ke backend sebagai string JSON
 * (bukan multipart upload).
 */
export function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            // "data:video/webm;base64,XXXX" → "XXXX"
            const commaIdx = result.indexOf(',');
            resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        };
        reader.onerror = () => reject(new Error('Gagal baca blob'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Helper kombinasi: kompres image (kalau input gambar) + upload direct.
 * Return URL publik yang siap disimpan.
 */
export async function compressAndUpload(
    file: File,
    purpose: UploadPurpose,
    options: {
        maxSize?: number;
        quality?: number;
        compressMime?: string;
    } = {}
): Promise<{ url: string; originalSize: number; finalSize: number }> {
    const originalSize = file.size;

    // Compress hanya kalau input gambar. Video liveness di-skip kompres.
    let blobToUpload: Blob = file;
    let contentType = file.type;

    if (file.type.startsWith('image/')) {
        const mime = options.compressMime ?? 'image/jpeg';
        blobToUpload = await compressImage(
            file,
            options.maxSize ?? 1920,
            options.quality ?? 0.85,
            mime
        );
        contentType = mime;
    }

    const url = await uploadDirect(blobToUpload, purpose, contentType);
    return { url, originalSize, finalSize: blobToUpload.size };
}
